import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
} from '@angular/core';
import { UnassignedRegionResizeService } from '../../services/unassigned-region-resize.service';

const IS_RESIZING_CLASS = 'is-resizing';
// Set while hovering the strip itself: CSS :hover would match the whole region.
const IS_RESIZE_HOVER_CLASS = 'is-resize-hover';

// Resizes the unassigned region from a `::before` strip that sits just above
// the host (see the shared unassigned region styling). The strip belongs to the
// host, so it can never overlap the region's drop zone and break its dragleave
// detection the way a separate handle element did.
//
// Only applied via `hostDirectives` on components that provide
// UnassignedRegionResizeService, hence no selector.
@Directive({})
export class UnassignedRegionResizeDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly ngZone = inject(NgZone);
  private readonly unassignedRegionResizeService = inject(
    UnassignedRegionResizeService,
  );

  private startClientY = 0;
  private lastDeltaY = 0;
  private isResizeHover = false;
  private removeHostListeners: (() => void)[] = [];
  private removePointerMoveListener: (() => void) | null = null;
  private removePointerUpListener: (() => void) | null = null;
  private removePointerCancelListener: (() => void) | null = null;

  // Registered outside Angular's zone so the drag never trips change
  // detection on its own — the resize service re-enters the zone only for
  // its throttled commits.
  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      const host = this.elementRef.nativeElement;
      this.removeHostListeners = [
        this.renderer.listen(host, 'pointerdown', this.onPointerDown),
        this.renderer.listen(host, 'pointermove', this.onHostPointerMove),
        this.renderer.listen(host, 'pointerleave', () =>
          this.setResizeHover(false),
        ),
      ];
    });
  }

  ngOnDestroy(): void {
    this.removeHostListeners.forEach((removeListener) => removeListener());
    this.stopTrackingDrag();
  }

  private onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 || !this.isOnResizeStrip(event)) {
      return;
    }
    event.preventDefault();
    // Guarantees pointerup/pointercancel arrive even if the pointer is released
    // outside the window or over an iframe, so the resize always ends.
    this.elementRef.nativeElement.setPointerCapture(event.pointerId);
    this.startClientY = event.clientY;
    this.lastDeltaY = 0;
    this.renderer.addClass(this.elementRef.nativeElement, IS_RESIZING_CLASS);
    this.unassignedRegionResizeService.onResizeStart();

    this.removePointerMoveListener = this.renderer.listen(
      'document',
      'pointermove',
      this.onPointerMove,
    );
    this.removePointerUpListener = this.renderer.listen(
      'document',
      'pointerup',
      this.onPointerUp,
    );
    this.removePointerCancelListener = this.renderer.listen(
      'document',
      'pointercancel',
      this.onPointerUp,
    );
  };

  private onPointerMove = (event: PointerEvent): void => {
    this.lastDeltaY = this.startClientY - event.clientY;
    this.unassignedRegionResizeService.onResize(this.lastDeltaY);
  };

  private onHostPointerMove = (event: PointerEvent): void => {
    this.setResizeHover(this.isOnResizeStrip(event));
  };

  private setResizeHover(isResizeHover: boolean): void {
    if (isResizeHover === this.isResizeHover) {
      return;
    }
    this.isResizeHover = isResizeHover;
    if (isResizeHover) {
      this.renderer.addClass(
        this.elementRef.nativeElement,
        IS_RESIZE_HOVER_CLASS,
      );
    } else {
      this.renderer.removeClass(
        this.elementRef.nativeElement,
        IS_RESIZE_HOVER_CLASS,
      );
    }
  }

  private onPointerUp = (): void => {
    this.stopTrackingDrag();
    this.renderer.removeClass(this.elementRef.nativeElement, IS_RESIZING_CLASS);
    this.unassignedRegionResizeService.onResizeEnd(this.lastDeltaY);
  };

  // Events on the `::before` strip target the host itself and land above its
  // top edge, whereas events on the region's content target a descendant.
  private isOnResizeStrip(event: PointerEvent): boolean {
    const host = this.elementRef.nativeElement;
    return (
      event.target === host && event.clientY < host.getBoundingClientRect().top
    );
  }

  private stopTrackingDrag(): void {
    this.removePointerMoveListener?.();
    this.removePointerUpListener?.();
    this.removePointerCancelListener?.();
    this.removePointerMoveListener = null;
    this.removePointerUpListener = null;
    this.removePointerCancelListener = null;
  }
}
